/**
 * Statusi narudžbe u ručnoj alokaciji (m.40.3, m.1): jedan niz, jedan status po narudžbi
 * (Tonći Šiklić, mail "promjena naziva u BIS u ručna alokacija", 01.10.2026).
 *
 * Mijenjaju se samo nazivi, vrijednosti annotation_type ostaju (isto pravilo kao imam_ju od 30.09.):
 *   none       = Bez statusa (nema statusne oznake)
 *   u_dolasku  = U obradi (naručeno kod dobavljača; "U dolasku" je ukinut)
 *   pakirano   = Pakirano
 *   na_putu    = Poslano (s centralnog prema poslovnici: GLS / Vozač / Pošta / Pohranjeno)
 *   poslano    = Poslano klijentu (HP/GLS, Vedran 30.09.; nije u Tonćijevom nizu, ostaje kako je)
 *   imam_ju    = Smješteno (u poslovnici, spremno za kupca; isti naziv kao u Farosu)
 *   isporuceno = ukinuto, novi se ne postavlja; postojeće oznake ostaju vidljive. Narudžbu zatvara završni račun.
 * Opis (help) je legenda koja se pokazuje kad se miš zadrži na statusu.
 */

export type OrderStatusKey = 'none' | 'u_dolasku' | 'pakirano' | 'na_putu' | 'poslano' | 'imam_ju' | 'isporuceno';

type Text = { hr: string; en: string };

export const ORDER_STATUS: Record<OrderStatusKey, { label: Text; help: Text; icon: string; color: string }> = {
    none: {
        label: { hr: 'Bez statusa', en: 'No status' },
        help: { hr: 'Narudžba unesena, još nije naručena kod dobavljača.', en: 'Order entered, not yet ordered from the supplier.' },
        icon: '·', color: '#6b7280',
    },
    u_dolasku: {
        label: { hr: 'U obradi', en: 'Processing' },
        help: { hr: 'Naručeno kod dobavljača, čeka se dolazak na centralno skladište (može trajati i tjednima).', en: 'Ordered from the supplier, waiting to arrive at the central warehouse (can take weeks).' },
        icon: '🛠', color: '#8a6817',
    },
    pakirano: {
        label: { hr: 'Pakirano', en: 'Packed' },
        help: { hr: 'Roba stigla na centralno, zapakirana, čeka slanje.', en: 'Goods arrived at central, packed, waiting to be sent.' },
        icon: '📦', color: '#b7791f',
    },
    na_putu: {
        label: { hr: 'Poslano', en: 'Sent' },
        help: { hr: 'Otišlo s centralnog prema poslovnici (GLS / Vozač / Pošta / Pohranjeno).', en: 'Left central for the branch (GLS / Driver / Post / Stored).' },
        icon: '🚚', color: '#1e40af',
    },
    poslano: {
        label: { hr: 'Poslano klijentu', en: 'Sent to customer' },
        help: { hr: 'Poslano s centralnog izravno klijentu (Pošta ili GLS).', en: 'Sent from central directly to the customer (Post or GLS).' },
        icon: '📤', color: '#6d28d9',
    },
    imam_ju: {
        label: { hr: 'Smješteno', en: 'Placed' },
        help: { hr: 'Roba je u poslovnici, spremna za isporuku kupcu (dosadašnje "Imam ju").', en: 'Goods are in the branch, ready for the customer (formerly "Imam ju").' },
        icon: '✋', color: '#1f7a36',
    },
    isporuceno: {
        label: { hr: 'Isporučeno (ukinuto)', en: 'Delivered (retired)' },
        help: { hr: 'Stari status, više se ne postavlja. Narudžbu zatvara završni račun i ona nestaje s popisa.', en: 'Retired status, no longer set. The final invoice closes the order and it leaves the list.' },
        icon: '✅', color: '#6b4f1c',
    },
};

/** Redoslijed niza; zadnji dosegnuti korak je aktivni status. */
export const ORDER_STATUS_SEQUENCE: OrderStatusKey[] = ['none', 'u_dolasku', 'pakirano', 'na_putu', 'poslano', 'imam_ju', 'isporuceno'];

/** Statusne vrijednosti annotation_type (bez virtualnog 'none'). */
export const ORDER_STATUS_TYPES: OrderStatusKey[] = ORDER_STATUS_SEQUENCE.filter((k) => k !== 'none');

/** Prijevoznik uz Poslano / Poslano klijentu; hp se prikazuje kao Pošta. */
export const CARRIER_LABEL: Record<string, Text> = {
    vozac: { hr: 'Vozač', en: 'Driver' }, hp: { hr: 'Pošta', en: 'Post' }, gls: { hr: 'GLS', en: 'GLS' },
    pohranjeno: { hr: 'Pohranjeno', en: 'Stored' }, centralno: { hr: 'Centralno', en: 'Central' },
};

const pick = (x: Text, locale: 'hr' | 'en') => (locale === 'en' ? x.en : x.hr);

export function orderStatusLabel(key: string, locale: 'hr' | 'en'): string {
    const s = ORDER_STATUS[key as OrderStatusKey];
    return s ? pick(s.label, locale) : key;
}

export function carrierLabel(value: string, locale: 'hr' | 'en'): string {
    const c = CARRIER_LABEL[value];
    return c ? pick(c, locale) : value;
}

/** Aktivni status iz oznaka narudžbe: zadnji dosegnuti korak niza. */
export function currentOrderStatus(annotations: Array<{ annotation_type: string }> | undefined | null): OrderStatusKey {
    const types = new Set((annotations ?? []).map((a) => a.annotation_type));
    for (let i = ORDER_STATUS_SEQUENCE.length - 1; i > 0; i--) {
        if (types.has(ORDER_STATUS_SEQUENCE[i])) return ORDER_STATUS_SEQUENCE[i];
    }
    return 'none';
}

/**
 * Tekst za title= na statusu: opis tog statusa pa cijeli niz, da se vidi gdje je narudžba.
 * Ukinuti Isporučeno se u legendi ne nabraja.
 */
export function orderStatusTooltip(key: string, locale: 'hr' | 'en'): string {
    const s = ORDER_STATUS[key as OrderStatusKey];
    const head = s ? `${pick(s.label, locale)}: ${pick(s.help, locale)}` : key;
    const legend = ORDER_STATUS_SEQUENCE.filter((k) => k !== 'isporuceno')
        .map((k, i) => `${i + 1}. ${pick(ORDER_STATUS[k].label, locale)} - ${pick(ORDER_STATUS[k].help, locale)}`)
        .join('\n');
    const close = locale === 'en' ? 'The final invoice closes the order and it leaves the list.' : 'Završni račun zatvara narudžbu i ona nestaje s popisa.';

    return `${head}\n\n${legend}\n\n${close}`;
}
